/**
 * services/ai/AIProviderManager.ts
 * AI provider manager singleton
 *
 * Coordinates credential retrieval (from AICredentialService) with provider
 * instantiation (via AIProviderFactory) to return ready-to-use AI provider
 * instances. No caching — each call fetches the latest decrypted key.
 *
 * Work Unit: WU04 - AI Provider Client Implementations
 * Task: T03 - Provider Factory & Manager
 * Sprint: S01 - AI Architecture & Infrastructure Foundation
 * Project: P09 - AI Integration
 */

// Internal absolute imports (type-only)
import type {
  AIKeyValidationResult,
  AIProvider,
  AIProviderType,
} from '@/types/shared/AI';

// Internal absolute imports (value) — direct import to avoid circular dependency
import { aiCredentialService } from '@/services/AICredentialService';

// Sibling imports (value)
import { createProvider } from './AIProviderFactory';

// ─── Manager ────────────────────────────────────────────────────────────────

/**
 * AI Provider Manager
 *
 * Singleton that bridges credential storage and provider instantiation.
 * Retrieves decrypted API keys from `AICredentialService` and creates
 * the appropriate provider via `createProvider()`.
 *
 * Provider instances are not cached — each call creates a fresh instance
 * with the latest decrypted key, ensuring key updates take effect immediately.
 *
 * @example
 * ```ts
 * const provider = await aiProviderManager.getProvider('openai');
 * if (provider) {
 *   const response = await provider.chat([{ role: 'user', content: 'Hello' }]);
 * }
 * ```
 */
class AIProviderManager {
  /**
   * Get a ready-to-use provider instance for the given provider type
   *
   * Retrieves the decrypted API key from credential storage and creates
   * a configured provider. Returns `null` if no key is stored for the
   * requested provider.
   *
   * @param provider - Which AI provider to get
   * @returns Configured AIProvider instance, or null if no key is configured
   */
  async getProvider(provider: AIProviderType): Promise<AIProvider | null> {
    const apiKey = await aiCredentialService.getDecryptedKey(provider);

    if (!apiKey) {
      return null;
    }

    return createProvider(provider, apiKey);
  }

  /**
   * Check if any AI provider has stored credentials
   *
   * @returns true if at least one provider has a saved API key
   */
  async isAnyProviderConfigured(): Promise<boolean> {
    const providers = await aiCredentialService.listProviders();
    return providers.length > 0;
  }

  /**
   * Get the list of providers that have stored credentials
   *
   * @returns Array of configured provider types
   */
  async getConfiguredProviders(): Promise<AIProviderType[]> {
    return aiCredentialService.listProviders();
  }

  /**
   * Validate an API key without saving credentials
   *
   * Creates a temporary provider instance with the provided key and
   * calls its `validateKey()` method. The key is never persisted —
   * use `AICredentialService.saveCredential()` to store it after
   * successful validation.
   *
   * @param provider - Which AI provider the key belongs to
   * @param apiKey - Plaintext API key to validate
   * @returns Validation result
   */
  async validateKey(
    provider: AIProviderType,
    apiKey: string,
  ): Promise<AIKeyValidationResult> {
    const tempProvider = createProvider(provider, apiKey);
    return tempProvider.validateKey(apiKey);
  }
}

// ─── Singleton Export ───────────────────────────────────────────────────────

export { AIProviderManager };
export const aiProviderManager = new AIProviderManager();
